import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Edit3, Trash2, Settings, Power, PowerOff, 
  Loader2, AlertTriangle, Save, X, ExternalLink
} from 'lucide-react';
import MCPToolService from '../../services/MCPToolService';

const FormBuilder = ({ form, setForm }) => {
  const [showAddField, setShowAddField] = useState(false);
  const [newField, setNewField] = useState({
    id: '',
    label: '',
    value: '',
    required: false,
    is_password: false,
    type: 'text',
    options: []
  });

  const fieldTypes = [
    { value: 'text', label: 'Texto' },
    { value: 'password', label: 'Senha' },
    { value: 'select', label: 'Seleção' },
    { value: 'json', label: 'JSON' }
  ];

  const addField = () => {
    if (!newField.id || !newField.label) return;
    
    const field = {
      ...newField,
      options: newField.type === 'select' ? newField.options : undefined
    };
    
    setForm([...form, field]);
    setNewField({
      id: '',
      label: '',
      value: '',
      required: false,
      is_password: false,
      type: 'text',
      options: []
    });
    setShowAddField(false);
  };

  const removeField = (index) => {
    setForm(form.filter((_, i) => i !== index));
  };

  const addOption = () => {
    setNewField(prev => ({
      ...prev,
      options: [...prev.options, { value: '', label: '' }]
    }));
  };

  const updateOption = (index, field, value) => {
    const options = [...newField.options];
    options[index][field] = value;
    setNewField(prev => ({ ...prev, options }));
  };

  const removeOption = (index) => {
    setNewField(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">Campos do Formulário</h4>
        <button
          type="button"
          onClick={() => setShowAddField(true)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
        >
          <Plus className="w-4 h-4 mr-1" />
          Adicionar Campo
        </button>
      </div>

      {form.length > 0 && (
        <div className="space-y-2">
          {form.map((field, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{field.label}</span>
                  <span className="text-xs text-gray-500">({field.type})</span>
                  {field.required && <span className="text-xs text-red-500">*</span>}
                  {field.is_password && <span className="text-xs text-yellow-600">🔒</span>}
                </div>
                <div className="text-xs text-gray-600">ID: {field.id}</div>
              </div>
              <button
                type="button"
                onClick={() => removeField(index)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Adicionar Campo</h3>
              <button
                onClick={() => setShowAddField(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID do Campo *
                </label>
                <input
                  type="text"
                  value={newField.id}
                  onChange={(e) => setNewField(prev => ({ ...prev, id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ex: api_key"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label *
                </label>
                <input
                  type="text"
                  value={newField.label}
                  onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ex: Chave da API"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={newField.type}
                  onChange={(e) => setNewField(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {fieldTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newField.required}
                    onChange={(e) => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Obrigatório</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newField.is_password}
                    onChange={(e) => setNewField(prev => ({ ...prev, is_password: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">É senha</span>
                </label>
              </div>

              {newField.type === 'select' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Opções
                    </label>
                    <button
                      type="button"
                      onClick={addOption}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      + Adicionar opção
                    </button>
                  </div>
                  <div className="space-y-2">
                    {newField.options.map((option, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Valor"
                          value={option.value}
                          onChange={(e) => updateOption(index, 'value', e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Label"
                          value={option.label}
                          onChange={(e) => updateOption(index, 'label', e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowAddField(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={addField}
                disabled={!newField.id || !newField.label}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MCPToolsAdmin = () => {
  const [tools, setTools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTool, setEditingTool] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    provider_title: '',
    provider_icon: '',
    version: '1.0.0',
    tags: '',
    mcp_server_url: '',
    enabled: true,
    form: []
  });

  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  const fetchTools = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await MCPToolService.getTools(getAuthToken(), {
        search: searchTerm,
        enabled: statusFilter !== 'all' ? statusFilter === 'enabled' : undefined
      });
      setTools(data);
    } catch (err) {
      console.error("Erro ao buscar ferramentas MCP:", err);
      setError(err.message || "Não foi possível carregar as ferramentas.");
      setTools([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      provider_title: '',
      provider_icon: '',
      version: '1.0.0',
      tags: '',
      mcp_server_url: '',
      enabled: true,
      form: []
    });
  };

  const handleCreate = () => {
    resetForm();
    setEditingTool(null);
    setShowCreateModal(true);
  };

  const handleEdit = (tool) => {
    setFormData({
      name: tool.name,
      description: tool.description,
      provider_title: tool.provider_title,
      provider_icon: tool.provider_icon,
      version: tool.version,
      tags: Array.isArray(tool.tags) ? tool.tags.join(', ') : '',
      mcp_server_url: tool.mcp_server_url,
      enabled: tool.enabled,
      form: tool.form || []
    });
    setEditingTool(tool);
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      const toolData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        type: 'mcp',
        provider: 'MCPServer'
      };

      if (editingTool) {
        await MCPToolService.updateTool(editingTool.id, toolData, getAuthToken());
      } else {
        await MCPToolService.createTool(toolData, getAuthToken());
      }

      setShowCreateModal(false);
      resetForm();
      await fetchTools();
    } catch (err) {
      setError(err.message || "Erro ao salvar ferramenta.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (toolId, toolName) => {
    if (!window.confirm(`Tem certeza que deseja excluir a ferramenta "${toolName}"?`)) {
      return;
    }

    try {
      setIsLoading(true);
      await MCPToolService.deleteTool(toolId, getAuthToken());
      await fetchTools();
    } catch (err) {
      setError(err.message || "Erro ao excluir ferramenta.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (tool) => {
    try {
      await MCPToolService.updateTool(tool.id, { enabled: !tool.enabled }, getAuthToken());
      await fetchTools();
    } catch (err) {
      setError(err.message || "Erro ao alterar status.");
    }
  };

  const filteredTools = tools.filter(tool => {
    const matchesSearch = !searchTerm || 
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'enabled' && tool.enabled) ||
      (statusFilter === 'disabled' && !tool.enabled);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Ferramentas MCP</h1>
              <p className="text-gray-600">Gerencie as ferramentas MCP disponíveis na plataforma.</p>
            </div>
            <button
              onClick={handleCreate}
              className="mt-4 md:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 rounded-lg text-white font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nova Ferramenta MCP
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total de Ferramentas</p>
                <h3 className="text-2xl font-bold text-gray-900">{tools.length}</h3>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-green-100 rounded-full p-3 mr-4">
                <Power className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Ferramentas Ativas</p>
                <h3 className="text-2xl font-bold text-gray-900">
                  {tools.filter(t => t.enabled).length}
                </h3>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-red-100 rounded-full p-3 mr-4">
                <PowerOff className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Ferramentas Inativas</p>
                <h3 className="text-2xl font-bold text-gray-900">
                  {tools.filter(t => !t.enabled).length}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Buscar ferramentas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Status</option>
              <option value="enabled">Apenas Ativas</option>
              <option value="disabled">Apenas Inativas</option>
            </select>
          </div>
        </div>

        {isLoading && (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto" />
            <p className="mt-2 text-gray-600">Carregando ferramentas...</p>
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <div>
              <p className='font-semibold'>Erro</p>
              <p className='text-sm'>{error}</p>
              <button onClick={fetchTools} className='mt-1 text-sm underline'>
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {filteredTools.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhuma ferramenta encontrada
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Tente ajustar os filtros de busca.' 
                    : 'Comece criando sua primeira ferramenta MCP.'
                  }
                </p>
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 rounded-lg text-white font-medium hover:bg-blue-700"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Nova Ferramenta MCP
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTools.map(tool => (
                  <div key={tool.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center">
                          {tool.provider_icon && (
                            <img
                              src={tool.provider_icon}
                              alt={tool.provider_title}
                              className="w-10 h-10 rounded-lg mr-3"
                            />
                          )}
                          <div>
                            <h3 className="font-semibold text-gray-900">{tool.name}</h3>
                            <p className="text-sm text-gray-500">{tool.provider_title}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleStatus(tool)}
                            className={`p-1 rounded ${
                              tool.enabled 
                                ? 'text-green-600 hover:bg-green-50' 
                                : 'text-gray-400 hover:bg-gray-50'
                            }`}
                            title={tool.enabled ? 'Desativar' : 'Ativar'}
                          >
                            {tool.enabled ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {tool.description}
                      </p>

                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs text-gray-500">v{tool.version}</span>
                        <div className="flex items-center gap-1">
                          <ExternalLink className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">MCP Server</span>
                        </div>
                      </div>

                      {tool.tags && tool.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {tool.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                          {tool.tags.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{tool.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(tool)}
                          className="flex-1 py-2 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1"
                        >
                          <Edit3 className="w-4 h-4" />
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(tool.id, tool.name)}
                          className="py-2 px-3 border border-red-300 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 flex items-center justify-center"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium">
                    {editingTool ? 'Editar Ferramenta MCP' : 'Nova Ferramenta MCP'}
                  </h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 space-y-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Informações Básicas</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Ferramenta *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="ex: Notion MCP Server"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descrição *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Descreva o que esta ferramenta faz..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Título do Provider *
                      </label>
                      <input
                        type="text"
                        value={formData.provider_title}
                        onChange={(e) => setFormData(prev => ({ ...prev, provider_title: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="ex: Notion"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Versão *
                      </label>
                      <input
                        type="text"
                        value={formData.version}
                        onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="ex: 1.0.0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL do Ícone *
                    </label>
                    <input
                      type="url"
                      value={formData.provider_icon}
                      onChange={(e) => setFormData(prev => ({ ...prev, provider_icon: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://exemplo.com/icon.png"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL do MCP Server *
                    </label>
                    <input
                      type="url"
                      value={formData.mcp_server_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, mcp_server_url: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://seu-mcp-server.azurewebsites.net/mcp/"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags (separadas por vírgula)
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="ex: notion, productivity, database"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enabled"
                      checked={formData.enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
                      Ferramenta ativa
                    </label>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <FormBuilder 
                    form={formData.form}
                    setForm={(form) => setFormData(prev => ({ ...prev, form }))}
                  />
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={!formData.name || !formData.description || !formData.mcp_server_url}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingTool ? 'Salvar Alterações' : 'Criar Ferramenta'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MCPToolsAdmin;